import React, { useState } from "react";
import { MARKETING_PANEL } from "@/lib/marketingPanel";
import { ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function BigCTA() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [err, setErr] = useState("");

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSuccess(true);
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (e2) {
      setErr(formatApiError(e2, "Failed to send"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      data-testid="big-cta-section"
      className="relative py-20 md:py-28 overflow-hidden"
    >
      <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />
      <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-[640px] h-[640px] rounded-full bg-blue-500/5 blur-3xl" />

      <div className="relative max-w-6xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Left: Big CTA */}
        <div className="text-center lg:text-left reveal">
          <p className="text-xs font-bold text-red-500 uppercase tracking-widest mb-4">
            Ready to save on every order?
          </p>
          <h2
            data-testid="big-cta-title"
            className="font-display font-black text-3xl md:text-5xl text-slate-900 leading-[1.1] tracking-tight mb-6"
          >
            Start your first live auction in{" "}
            <span className="text-blue-600">under 3 minutes.</span>
          </h2>
          <p className="text-base md:text-lg text-slate-600 mb-8 max-w-md mx-auto lg:mx-0">
            Create an account, launch a tender, and receive competitive offers
            from verified manufacturers the same week.
          </p>

          <div className="flex flex-wrap gap-3 justify-center lg:justify-start">
            <a
              href={MARKETING_PANEL.createBid}
              data-testid="big-create-bid-btn"
              className="group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-gradient-to-br from-blue-600 to-blue-700 text-white font-display font-black text-lg md:text-xl shadow-2xl shadow-blue-600/30 hover:shadow-blue-600/50 hover:brightness-110 btn-press cta-pulse"
            >
              Create a Bid
              <ArrowRight
                size={22}
                className="group-hover:translate-x-1 transition-transform"
              />
            </a>
            <a
              href={MARKETING_PANEL.signIn}
              data-testid="big-signin-btn"
              className="inline-flex items-center gap-2 px-6 py-4 rounded-full bg-white text-slate-800 font-bold border border-slate-200 hover:border-blue-300 hover:text-blue-700 btn-press"
            >
              I already have an account
            </a>
          </div>

          <p className="mt-6 text-sm text-slate-500 font-medium">
            No credit card required · Verified suppliers only · Cancel anytime
          </p>
        </div>

        {/* Right: Contact / Demo form */}
        <div
          data-testid="contact-form-wrapper"
          className="reveal bg-white rounded-3xl shadow-xl shadow-slate-900/5 border border-slate-100 p-6 md:p-8"
        >
          <p className="text-xs font-bold text-blue-600 uppercase tracking-widest mb-3">
            Or request a demo
          </p>
          <h3 className="font-display font-extrabold text-xl md:text-2xl text-slate-900 mb-5">
            Tell us what you source — we&apos;ll reach out.
          </h3>

          {success ? (
            <div
              data-testid="contact-success"
              className="flex items-start gap-3 p-4 rounded-xl bg-green-50 border border-green-200 text-green-800"
            >
              <CheckCircle2 className="mt-0.5 flex-shrink-0" size={20} />
              <div>
                <p className="font-bold">Thanks — message received.</p>
                <p className="text-sm mt-0.5">
                  Our team will get back to you within 1 business day.
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <input
                  data-testid="contact-name"
                  required
                  minLength={2}
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  placeholder="Full name"
                  className="px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
                <input
                  data-testid="contact-company"
                  name="company"
                  value={form.company}
                  onChange={onChange}
                  placeholder="Company (optional)"
                  className="px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
              <input
                data-testid="contact-email"
                required
                type="email"
                name="email"
                value={form.email}
                onChange={onChange}
                placeholder="Work email"
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
              <textarea
                data-testid="contact-message"
                required
                minLength={5}
                name="message"
                value={form.message}
                onChange={onChange}
                rows={3}
                placeholder="What commodities do you source? Volume, frequency, etc."
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
              />
              {err && (
                <div
                  data-testid="contact-error"
                  className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 font-medium"
                >
                  {err}
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold btn-press disabled:opacity-60"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                Request a demo
              </button>
              <p className="text-xs text-slate-500 text-center">
                We respect your inbox — no spam, ever.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
